import type { Order, OrderDetail, OrderItem } from '../auth/types'
import type { EngineerOrder } from '../types'

/**
 * Date shown as "Expected collection/delivery": the day the order is booked
 * with the supplier (DEV-51011), so a chosen date shows instead of the
 * supplier's earliest. Falls back to the supplier's date for suppliers that
 * are sent no date, and for responses that predate the field.
 */
function displayFulfillmentDate(order: Order): string | null {
  return order.booked_fulfillment_date ?? order.expected_fulfillment_date ?? null
}

/**
 * Maps backend Order to frontend admin display format
 */
export function mapOrderForAdmin(order: Order) {
  return {
    id: order.id,
    order_number: order.order_number || order.id,
    transaction_id: order.transaction_id || order.id.substring(0, 8).toUpperCase(),
    status: order.status,
    supplier_status: order.supplier_status || 'pending',
    supplier_name: order.supplier_name || 'Unknown supplier',
    branch_name: order.branch_name || 'Unknown branch',
    total: parseFloat(order.total_amount),
    item_count: order.items?.length || 0,
    placed_by: order.placed_by_name || 'Unknown user',
    placed_by_email: order.placed_by_email || '',
    placed_by_phone: order.placed_by_phone || '',
    placed_by_role: order.placed_by_role || '',
    business_area: (order as Order & { business_area?: string | null }).business_area || '',
    placed_at: order.created_at,
    fulfillment: order.fulfillment_type || 'collection',
    approval_status: mapApprovalStatus(order.status),
    approved_by: order.approved_by_name || order.approved_by || null,
    // Assigned approver (who the order is routed to) — distinct from
    // approved_by (who actually acted). Used by the approval queue.
    assigned_approver: order.approver_name || null,
    approval_reasons: order.approval_reasons ?? [],
    reason: order.reason || null,
    secondary_reason: order.secondary_reason || null,
    is_restricted: (order.items || []).some((item) => item.product_snapshot?.is_restricted),
    status_available: order.status_available ?? true,
    status_warning_message: order.status_warning_message || null,
  }
}

/**
 * Maps backend OrderDetail to frontend admin detail format
 */
export function mapOrderDetail(order: OrderDetail) {
  return {
    ...mapOrderForAdmin(order),
    expected_fulfillment_date: displayFulfillmentDate(order),
    job_number: order.job_number || 'N/A',
    reason: order.reason || null,
    secondary_reason: order.secondary_reason || null,
    general_notes: order.notes || null,
    engineer_name: order.engineer_name || null,
    engineer_team: order.engineer_team || null,
    assigned_approver: order.approver_name || null,
    branch_address: order.branch_details
      ? [order.branch_details.address, order.branch_details.postcode]
          .filter(Boolean)
          .join(', ') || 'N/A'
      : order.branch_address || 'N/A',
    branch_phone: order.branch_details?.phone || order.branch_phone || null,
    delivery_address: order.line_1
      ? {
          line_1: order.line_1,
          line_2: order.line_2 || '',
          line_3: order.line_3 || '',
          post_town: order.post_town || '',
          postcode: order.postcode || '',
        }
      : null,
    items: order.items.map((item) => {
      // Use product_snapshot as primary source for historical accuracy
      const snapshot = item.product_snapshot
      const unitPrice = snapshot?.price ?? parseFloat(item.unit_price)
      // placed_quantity is set once an order is approved with stock capping
      // (AC4). Reflect it so the post-approval detail (qty) matches the backend;
      // null pre-approval → the requested quantity (Codex P2). unit_price stays
      // the original for the reduced-row "was" reference.
      const placedQuantity = (item as OrderItem & { placed_quantity?: number | null }).placed_quantity
      const effectiveQuantity = placedQuantity ?? item.quantity
      // Use the backend's stored line total (re-priced to the approval-time price
      // and rounded once at full precision) rather than recomputing from the
      // 2-decimal unit price, so the rows always sum to the order total (Codex
      // P2). Falls back to unitPrice × qty only for older data with no stored total.
      const rawLineTotal = (item as OrderItem & { total_price?: string | null }).total_price
      const lineTotal = rawLineTotal != null ? parseFloat(rawLineTotal) : unitPrice * effectiveQuantity
      return {
        id: item.id.toString(),
        product_name: snapshot?.name || item.product_name || 'Unknown product',
        product_code: item.manufacturer_code || snapshot?.manufacturer_code || snapshot?.supplier_product_code || item.product_id || 'N/A',
        quantity: effectiveQuantity,
        requested_quantity: item.quantity,
        unit_price: unitPrice,
        total_price: lineTotal,
        current_price:
          (item as OrderItem & { current_price?: string | null }).current_price != null
            ? parseFloat((item as OrderItem & { current_price?: string | null }).current_price as string)
            : null,
        image_url: snapshot?.image_url || null,
        unit_of_measure: snapshot?.unit_of_measure || '-',
        is_restricted: snapshot?.is_restricted ?? false,
        branch_stock: (item as OrderItem & { branch_stock?: number | null }).branch_stock ?? null,
        branch_stock_at_order:
          (item as OrderItem & { branch_stock_at_order?: number | null }).branch_stock_at_order ??
          null,
        dc_stock: (item as OrderItem & { dc_stock?: number | null }).dc_stock ?? null,
        // Overlaid from live-pricing on pending orders (getStockState uses it).
        cap_total: null as number | null,
        // Backorder tier, overlaid from live-pricing on pending orders: an
        // allowed line is placed in full even above stock (single date).
        backorder_allowed: false as boolean,
        backorder_date: null as string | null,
        availability: (item as OrderItem & { availability?: string | null }).availability || null,
        current_stock:
          (item as OrderItem & { current_stock?: number | null }).current_stock ??
          (item as OrderItem & { branch_stock?: number | null }).branch_stock ??
          null,
        is_van_stock: item.is_van_stock ?? false,
        // Declined because it couldn't be fulfilled: the decline snapshot recorded
        // 0 orderable stock for this line (APR-005 AC7).
        unavailable_at_decision:
          order.status === 'internal_decline' &&
          (item as OrderItem & { stock_at_submission?: number | null }).stock_at_submission === 0,
      }
    }),
    notes: order.notes_list.map((note) => ({
      id: note.id.toString(),
      author: note.author || 'System',
      text: note.text,
      created_at: note.created_at,
    })),
    approved_at: order.approved_at,
    decline_reason: order.disapproval_reason,
    approval_context: order.approval_context ?? {},
    status_available: order.status_available ?? true,
    status_warning_message: order.status_warning_message || null,
  }
}

/**
 * Maps backend OrderDetail to engineer mobile detail format with correct field names
 */
export function mapOrderDetailForEngineer(order: OrderDetail) {
  return {
    id: order.id,
    orderNumber: order.order_number || order.id.substring(0, 8),
    transactionId: order.transaction_id || undefined,
    status: order.status as EngineerOrder['status'],
    supplierId: order.supplier_id ?? 0,
    branchId: order.branch_id ?? '',
    supplierName: order.supplier_name || 'Unknown supplier',
    supplierIsActive: order.supplier_is_active ?? true,
    branchName: order.branch_name || 'Unknown branch',
    fulfillmentType: order.fulfillment_type === 'delivery' ? ('delivery' as const) : ('pickup' as const),
    expectedFulfillmentDate: displayFulfillmentDate(order),
    hasRequestedDate: !!order.requested_fulfillment_date,
    isFulfillmentDateUnconfirmed: !order.expected_fulfillment_date,
    collectionGuidance: order.collection_guidance ?? null,
    deliveryGuidance: order.delivery_guidance ?? null,
    total: parseFloat(order.total_amount),
    itemCount: order.items?.length || 0,
    placedAt: order.created_at,
    submittedAt: order.submitted_at ?? null,
    supplierOrderId: order.supplier_order_id ?? null,
    jobNumber: order.job_number || undefined,
    reasonForOrder: order.reason || undefined,
    secondaryReason: order.secondary_reason || undefined,
    supplierStatus: order.supplier_status || undefined,
    disapprovalReason: order.disapproval_reason || undefined,
    assigned_approver: order.approver_name || null,
    approved_by: order.approved_by_name || null,
    notes: order.notes || undefined,
    statusAvailable: order.status_available ?? true,
    statusWarningMessage: order.status_warning_message || undefined,
    items: order.items.map((item) => {
      // Use product_snapshot as primary source for historical accuracy
      const snapshot = item.product_snapshot
      // Reflect the actually-placed quantity once an order is approved with
      // stock capping (AC4), so the engineer's detail (linked from the approval
      // email) matches the supplier submission + confirmation email; null
      // pre-approval → the requested quantity (Codex P2).
      const itemWithApproval = item as OrderItem & {
        placed_quantity?: number | null
        expected_date_at_order?: string | null
        expected_date_at_submission?: string | null
        unit_price_at_submission?: string | null
        total_price?: string | null
        stock_at_submission?: number | null
      }
      const placedQuantity = itemWithApproval.placed_quantity
      // Price the order was placed at (re-priced to the live approval-time price;
      // APR-017 AC2). Falls back to the original when none was captured.
      const originalPrice = snapshot?.price ?? parseFloat(item.unit_price)
      const chargedPrice =
        itemWithApproval.unit_price_at_submission != null
          ? parseFloat(itemWithApproval.unit_price_at_submission)
          : originalPrice
      // Backend line total (re-priced, full-precision, rounded once) so the line
      // totals always sum to the order total rather than drifting from a 2-decimal
      // unit price × qty recompute; falls back for older data (Codex P2).
      const lineTotal =
        itemWithApproval.total_price != null
          ? parseFloat(itemWithApproval.total_price)
          : chargedPrice * (placedQuantity ?? item.quantity)
      return {
        productId: snapshot?.raw_product_id || parseInt(item.id.toString()) || 0,
        productName: snapshot?.name || item.product_name || 'Unknown product',
        supplierProductCode: snapshot?.supplier_product_code || item.product_id || 'N/A',
        manufacturerCode: item.manufacturer_code || snapshot?.manufacturer_code || null,
        brand: item.brand || snapshot?.brand || null,
        price: chargedPrice,
        lineTotal,
        quantity: placedQuantity ?? item.quantity,
        // Requested vs placed drives the out-of-stock marker (APR-011 AC3):
        // placedQuantity === 0 means the line went out of stock at approval.
        requestedQuantity: item.quantity,
        placedQuantity: placedQuantity ?? null,
        // Declined because it couldn't be fulfilled: the decline snapshot recorded
        // 0 orderable stock for this line (AC7). placedQuantity is null on a
        // declined order, so this drives the out-of-stock marker instead.
        unavailableAtDecision:
          order.status === 'internal_decline' &&
          itemWithApproval.stock_at_submission === 0,
        // Per-item expected date the engineer saw at checkout vs recomputed at
        // approval — drives the per-item date-change / "Today" display
        // (APR-011 AC1/AC2). Null pre-approval or when no date was available.
        expectedDateAtOrder: itemWithApproval.expected_date_at_order ?? null,
        expectedDateAtSubmission: itemWithApproval.expected_date_at_submission ?? null,
        imageUrl: snapshot?.image_url || undefined,
        unitOfMeasure: snapshot?.unit_of_measure || '-',
        description: snapshot?.description || undefined,
        isVanStock: item.is_van_stock ?? false,
      }
    }),
    branch: order.branch_details ? {
      address: order.branch_details.address || '',
      postcode: order.branch_details.postcode || '',
      phone: order.branch_details.phone || '',
      email: order.branch_details.email || '',
      latitude: order.branch_details.latitude ?? null,
      longitude: order.branch_details.longitude ?? null,
    } : undefined,
    cancelEntry: order.cancel_entry ?? null,
    cancellationContactEmail: order.cancellation_contact_email ?? '',
    supplierAccountNumber: order.supplier_account_number || '',
    cancellationRequest: order.cancellation_request
      ? {
          reason: order.cancellation_request.reason,
          reasonLabel: order.cancellation_request.reason_label,
          note: order.cancellation_request.note,
          sentAt: order.cancellation_request.sent_at,
          to: order.cancellation_request.to,
          cc: order.cancellation_request.cc,
          confirmed: order.cancellation_request.confirmed,
          confirmedAt: order.cancellation_request.confirmed_at,
        }
      : null,
    deliveryAddress: order.line_1 ? {
      line_1: order.line_1,
      line_2: order.line_2 || '',
      line_3: order.line_3 || '',
      post_town: order.post_town || '',
      postcode: order.postcode || '',
    } : undefined,
  }
}

/**
 * Maps backend Order to engineer mobile display format
 */
export function mapOrderForEngineer(order: Order) {
  return {
    id: order.id,
    orderNumber: order.order_number || order.id.substring(0, 8),
    status: order.status as EngineerOrder['status'],
    supplierId: order.supplier_id ?? 0,
    branchId: order.branch_id ?? '',
    supplierName: order.supplier_name || 'Unknown supplier',
    supplierIsActive: order.supplier_is_active ?? true,
    branchName: order.branch_name || 'Unknown branch',
    fulfillmentType: order.fulfillment_type === 'delivery' ? ('delivery' as const) : ('pickup' as const),
    expectedFulfillmentDate: displayFulfillmentDate(order),
    collectionGuidance: order.collection_guidance ?? null,
    deliveryGuidance: order.delivery_guidance ?? null,
    total: parseFloat(order.total_amount),
    itemCount: order.item_count ?? order.items?.length ?? 0,
    placedAt: order.created_at,
    submittedAt: order.submitted_at ?? null,
    supplierOrderId: null,
    assigned_approver: order.approver_name || null,
    disapprovalReason: order.disapproval_reason || undefined,
    // APR-011 AC4: per-order summary of what changed at approval, for the
    // engineer order-list note. Provided by the list serializer annotations.
    outOfStockCount:
      (order as Order & { out_of_stock_count?: number }).out_of_stock_count ?? 0,
    dateChangedCount:
      (order as Order & { date_changed_count?: number }).date_changed_count ?? 0,
    items: [],  // Items not needed in list view, will be fetched in detail view
  }
}

/**
 * Maps backend order status to approval status
 */
function mapApprovalStatus(status: string): string {
  if (status === 'awaiting_approval' || status === 'pending_approval') return 'pending'
  if (status === 'approved') return 'approved'
  if (status === 'internal_decline' || status === 'rejected') return 'declined'
  return 'not_required'
}

/**
 * Capitalizes an internal status for display (e.g. "awaiting_supplier" → "Awaiting Supplier").
 */
export function formatStatus(status: string): string {
  return status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}


